import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Table } from "react-bootstrap";

import { EmptyState, formatDate, StatusBadge } from "../../layout/secure/common";
import {
  messageFromError,
  requestWordPressInstallationRotation,
  revokeWordPressInstallation,
  wordpressInstallations,
} from "../../utils/api";

export default function WordPressInstallations() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const result = await wordpressInstallations();
      setItems(Array.isArray(result?.items) ? result.items : []);
    } catch (requestError) {
      setError(messageFromError(requestError, "WordPress installations could not be loaded."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rotate = async item => {
    if (!window.confirm(`Request a private-secret rotation for ${item.canonicalOrigin}?`)) return;
    setBusy(item.publicId);
    setError("");
    try {
      await requestWordPressInstallationRotation(item.publicId);
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The rotation request could not be saved."));
    } finally {
      setBusy("");
    }
  };

  const revoke = async item => {
    if (!window.confirm(`Disconnect ${item.canonicalOrigin}? Its survey embeds will become unavailable until reconnected.`)) return;
    setBusy(item.publicId);
    setError("");
    try {
      await revokeWordPressInstallation(item.publicId);
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, "The WordPress installation could not be disconnected."));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="fera-panel mt-4" aria-labelledby="wordpress-installations-heading">
      <div className="fera-panel-heading">
        <div>
          <p className="fera-eyebrow">WordPress</p>
          <h2 id="wordpress-installations-heading">Connected installations</h2>
        </div>
        <Button variant="outline-primary" size="sm" onClick={load}>Refresh</Button>
      </div>
      <p className="text-muted">
        Each site has its own private server credential. FeraSurvey shows only the
        non-secret key fingerprint; rotation is completed by a WordPress administrator.
      </p>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!items.length ? (
        <EmptyState
          icon="web"
          title="No WordPress installations"
          detail="Connect the FeraSurvey plugin from a WordPress administration screen."
        />
      ) : (
        <Table responsive className="align-middle mb-0">
          <thead>
            <tr>
              <th>Site</th>
              <th>Status</th>
              <th>Key</th>
              <th>Last used</th>
              <th><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.publicId}>
                <td>
                  <strong>{item.canonicalOrigin}</strong>
                  <small className="d-block text-muted">{item.pluginVersion ? `Plugin ${item.pluginVersion}` : "Plugin version unknown"}</small>
                </td>
                <td>
                  <StatusBadge status={item.status} />
                  {item.rotationRequestedAt ? <small className="d-block text-warning">Rotation requested</small> : null}
                </td>
                <td><code>{item.secretFingerprint ? `${item.secretFingerprint.slice(0, 8)}…` : "Reconnect required"}</code></td>
                <td>{formatDate(item.lastUsedAt || item.connectedAt)}</td>
                <td>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={busy === item.publicId || item.status !== "active" || Boolean(item.rotationRequestedAt)}
                      onClick={() => rotate(item)}
                    >
                      Rotate secret
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={busy === item.publicId || item.status === "revoked"}
                      onClick={() => revoke(item)}
                    >
                      Disconnect
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
